import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import hand from "../../assets/mano.png";
import esquema1 from "../../assets/scenario4-esquema1.png";
import esquema2 from "../../assets/scenario4-esquema2.png";
import NextButton from "../../components/NextButton";
import ScenarioBar from "../../components/ScenarioBar";
import { getTexts } from "../../api/text";
import { SERVER_URL } from "../../api";
import { updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { ROUTES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import { modalGenericSmall } from "../../utils/logger";

function buildImageUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

const DEFAULT_QUESTION = "¿Cuál crees que es el mejor tratamiento para la paciente en este momento?";
const DEFAULT_SUBTITLE = "Selecciona el tratamiento más adecuado";
const DEFAULT_MODAL_LINE1 = "<p>&nbsp;</p><p><span style=\"font-size:18px;\"><strong>Has elegido prescribir&nbsp;</strong></span></p><p><span style=\"color:hsl(240,75%,60%);font-size:18px;\"><strong>{{OPTION}}</strong></span><span style=\"font-size:18px;\">.</span></p>";
const DEFAULT_MODAL_LINE2 = "<p class=\"txt-black\">Veamos cómo evoluciona Martina.</p>";

const DEFAULT_OPTION1_CONTENT1 = "<ul><li class=\"mt-3\">Bolo de metilprednisolona 0,25-0,5 g/día durante 3 días (y posterior pauta descendente)</li><li class=\"mt-2\">Micofenolato de mofetilo 2-3 g/día</li><li class=\"mt-2\">Hidroxicloroquina a ≤5 mg/kg/día (hasta un máximo de 400 mg/d)</li></ul>";
const DEFAULT_OPTION2_CONTENT1 = "<ul><li class=\"mt-3\">Bolo de metilprednisolona 0,25-0,5 g/día durante 2 días (y posterior pauta descendente)</li><li class=\"mt-2\">Micofenolato de mofetilo 2 g/día</li><li class=\"mt-2\">Hidroxicloroquina a ≤5 mg/kg/día (hasta un máximo de 400 mg/d)</li><li class=\"mt-2\">Voclosporina: 23,7 mg/12 h</li></ul>";

const DEFAULT_OPTIONS = [
	{
		id: 1,
		text: "Doble terapia",
		correct: false,
		content1Html: DEFAULT_OPTION1_CONTENT1,
		content2Title: "Regímenes o pautas de corticoides para pacientes con nefritis lúpica",
		content2Citation: "Rojas-Rivera JE, García-Carro C, Ávila A, et al. Documento de consenso del Grupo de Estudio de Enfermedades Glomerulares de la Sociedad Española de Nefrología (GLOSEN) para el diagnóstico y tratamiento de la nefritis lúpica. Nefrología. 2023;43:6-47.",
		imageSrc: esquema1,
	},
	{
		id: 2,
		text: "Triple terapia",
		correct: false,
		content1Html: DEFAULT_OPTION2_CONTENT1,
		content2Title: "Esquema de reducción de glucocorticoides",
		content2Citation: "Nota: las dosis de corticoides definidas por protocolo dependían del peso de los pacientes (≥45 kg y <45kg). Se grafican sólo las dosis para pacientes ≥45 kg. En la parte superior de la diapositiva se incluyen todas las dosis con independencia del peso<br />Rovin BH, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021 May 29;397:2070-2080",
		imageSrc: esquema2,
	},
];

function stripHtml(html) {
	if (!html || typeof html !== "string") return "";
	const div = document.createElement("div");
	div.innerHTML = html;
	return (div.textContent || div.innerText || "").trim();
}

function Scenario4({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state?.group);

	const scenarioNum = getScenarioByScreen(location.pathname);

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [question, setQuestion] = useState(DEFAULT_QUESTION);
	const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
	const [modalLine1, setModalLine1] = useState(DEFAULT_MODAL_LINE1);
	const [modalLine2, setModalLine2] = useState(DEFAULT_MODAL_LINE2);
	const [options, setOptions] = useState([]);
	const [optionsLoading, setOptionsLoading] = useState(true);

	useEffect(() => {
		const gameId = group?.game;
		if (!gameId) {
			setOptions(DEFAULT_OPTIONS);
			setOptionsLoading(false);
			return;
		}
		getTexts({ game: gameId })
			.then((res) => {
				if (res.status !== 200 || !Array.isArray(res.data)) {
					setOptions(DEFAULT_OPTIONS);
					setOptionsLoading(false);
					return;
				}
				const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});
				if (map["scenario4.question"] != null) setQuestion(map["scenario4.question"]);
				if (map["scenario4.subtitle"] != null) setSubtitle(map["scenario4.subtitle"]);
				if (map["scenario4.modalLine1"] != null) setModalLine1(map["scenario4.modalLine1"]);
				if (map["scenario4.modalLine2"] != null) setModalLine2(map["scenario4.modalLine2"]);
				const o1 = DEFAULT_OPTIONS[0];
				const o2 = DEFAULT_OPTIONS[1];
				setOptions([
					{
						...o1,
						text: map["scenario4.option1.label"] ?? o1.text,
						content1Html: map["scenario4.option1.content1"] ?? o1.content1Html,
						content2Title: map["scenario4.option1.content2Title"] ?? o1.content2Title,
						content2Citation: map["scenario4.option1.content2Citation"] ?? o1.content2Citation,
						imageSrc: buildImageUrl(map["scenario4.option1.image"]) || o1.imageSrc,
					},
					{
						...o2,
						text: map["scenario4.option2.label"] ?? o2.text,
						content1Html: map["scenario4.option2.content1"] ?? o2.content1Html,
						content2Title: map["scenario4.option2.content2Title"] ?? o2.content2Title,
						content2Citation: map["scenario4.option2.content2Citation"] ?? o2.content2Citation,
						imageSrc: buildImageUrl(map["scenario4.option2.image"]) || o2.imageSrc,
					},
				]);
				setOptionsLoading(false);
			})
			.catch(() => {
				setOptions(DEFAULT_OPTIONS);
				setOptionsLoading(false);
			});
	}, [group?.game]);

	const SelectOption = ({ id }) => {
		let option = options.find((x) => x.id === id);

		function handleOptionClick() {
			if (isCompleted) return;

			setSelectedOption(id);
		}

		const selected = selectedOption === id;

		function getBackgroundColor() {
			return selected && option?.correct && isCompleted
				? "#D6D431"
				: selected && !option?.correct && isCompleted
				? "#FF5948"
				: selected
				? "#EAF886"
				: "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex flex-column align-items-center justify-content-center txt-turquoise text-center mt-3 ${
						selected ? "option-selected" : ""
					}`}
					style={{
						position: "relative",
						height: "20vh",
						borderRadius: "10px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					<div className="bold" dangerouslySetInnerHTML={{ __html: option?.text || "" }} />
				</div>
			</div>
		);
	};

	function handleSelect() {
		const option = options?.find((x) => x.id === selectedOption);
		const optionPlain = option?.text ? stripHtml(option.text).toLowerCase() : "";
		const line1Html = (modalLine1 || DEFAULT_MODAL_LINE1).replace(/\{\{OPTION\}\}/g, optionPlain);
		const line2Html = modalLine2 || DEFAULT_MODAL_LINE2;
		const content = `<div class="text-center">${line1Html}${line2Html}</div>`;

		modalGenericSmall({ html: content }).then((res) => {
			if (res.isConfirmed) {
				if (selectedOption === 1)
					handleNavigate(ROUTES.TRANSITION_SCENARIOS);
				else handleNavigate(ROUTES.SCENARIO5_PART2_INTRO);
			}
		});
	}

	function handleNavigate(navigateTo) {
		let totalTime = getTimer();
		dispatch(updateTime(totalTime));

		updateStats({
			...group,
			time: totalTime,
			actualScenario: scenarioNum + 1,
			lastVisited: navigateTo,
		});
		navigate(navigateTo, { state: { activeScenario: scenarioNum + 1 } });
	}

	const SelectedOptionInfo = ({ id }) => {
		const option = options?.find((x) => x.id === id);
		if (!option) return null;
		return (
			<div>
				<div className="row d-flex align-items-start txt-white">
					<div className="col-4">
						<div className="bold txt20" dangerouslySetInnerHTML={{ __html: option.text || "" }} />
						<div className="ms-2" dangerouslySetInnerHTML={{ __html: option.content1Html || "" }} />
					</div>
					<div className={`col-8 ${id !== 2 ? "ps-5" : ""}`}>
						<div className="txt-white">
							<div className="bold" dangerouslySetInnerHTML={{ __html: option.content2Title || "" }} />
							<img className="my-2" width="100%" src={option.imageSrc} alt="" />
							<div className="txt8" style={{ lineHeight: "10px" }} dangerouslySetInnerHTML={{ __html: option.content2Citation || "" }} />
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					{selectedOption ? (
						<div>
							<SelectedOptionInfo id={selectedOption} />
						</div>
					) : optionsLoading && options.length === 0 ? (
						<div className="d-flex justify-content-center align-items-center my-4">
							<div className="txtWhite">Cargando contenido…</div>
						</div>
					) : (
						<>
							<div className="d-flex justify-content-center align-items-center my-4">
								<div className="col-7 text-center">
									<div className="txtWhite txt20 bold" dangerouslySetInnerHTML={{ __html: question || DEFAULT_QUESTION }} />
									<div className="d-flex align-items-center justify-content-center txtWhite mt-4">
										<img className="me-2" src={hand} alt="" />
										<div dangerouslySetInnerHTML={{ __html: subtitle || DEFAULT_SUBTITLE }} />
									</div>
								</div>
							</div>
							<div className="d-flex justify-content-center align-items-center">
								<div className="row col-7">
									{options.map((option) => (
										<div key={option.id} className="col-6">
											<SelectOption id={option.id} />
										</div>
									))}
								</div>
							</div>
						</>
					)}
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "0",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					{!isCompleted ? (
						<>
							{selectedOption && (
								<div
									className="btn-main-cancel-white me-3"
									onClick={() => setSelectedOption(null)}
								>
									<div className="text-btn-main">VOLVER</div>
								</div>
							)}
							<div
								className="btn-main"
								onClick={() => handleSelect()}
							>
								<div className="text-btn-main">SELECCIONAR</div>
							</div>
						</>
					) : (
						<NextButton navigateTo={ROUTES.SCENARIO2_RESULT} />
					)}
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario4);
